import { useState, useRef } from "react";
import { useAppContext } from "../app-context/provider";
import { ProductVariant } from "../../use-cases/contracts/ProductVariant";
import { useCart } from "~/components/cart/useCart";
import { Dimensions } from "~/use-cases/contracts/Dimensions";
import { useKlaviyo } from "./aplia/Klaviyo";

export type VariantPack = VariantPackItem[];

export type VariantPackItem = {
  variant: ProductVariant;
  quantity: number;
  dimensions?: Dimensions;
  key?: string; // a pack can have multiple items with the same variant...
};

export const AddToCartBtn: React.FC<{
  pack: VariantPack;
  label?: string;
}> = ({ pack, label = "addToCart" }) => {
  const [showTada, setShowTada] = useState(false);
  const [showOhNo, setShowOhNo] = useState(false);
  const { dispatch: contextDispatch, _t } = useAppContext();
  const cart = useCart();
  const { getKlaviyoUser } = useKlaviyo();

  const qty = useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = useState(1);
  const incrementValue = () => {
    setInputValue(inputValue + 1);
  };
  const deincrementValue = () => {
    setInputValue(inputValue <= 1 ? 1 : inputValue - 1);
  };

  const handleClick = async () => {
    const quantity = qty.current ? Number(qty.current.value) : 1;
    const user = getKlaviyoUser();

    // GA4 event
    const totalValue = pack.reduce((total, packitem) => {
      return total + (packitem.variant.priceVariants?.default?.value || 0) * quantity;
    }, 0);
    window.dataLayer?.push({
      event: "add_to_cart",
      ecommerce: {
        currency: "NOK",
        value: totalValue,
        items: pack.map((packitem) => ({
          item_id: packitem.variant.sku,
          item_name: packitem.variant.name,
          price: packitem.variant.priceVariants?.default?.value || 0,
          quantity,
        })),
      },
    });

    setShowTada(true);
    setShowOhNo(false);

    const results = await Promise.all(
      pack.map((packitem) => cart.add(packitem.variant.sku, quantity, user?.email)),
    );

    const firstFailure = results.find((r) => !r.success) as
      | { success: false; reason: string }
      | undefined;

    if (firstFailure) {
      setShowTada(false);
      setShowOhNo(true);
      if (firstFailure.reason === "OTC") {
        contextDispatch.addOtcRejectItems(pack.map((p) => p.variant));
      } else {
        contextDispatch.addOutOfStockRejectItems(pack.map((p) => p.variant));
      }
    } else {
      contextDispatch.addItemsToCart(pack.map((p) => p.variant));
    }

    setTimeout(() => {
      setShowTada(false);
      setShowOhNo(false);
    }, 1500);
  };

  const defaultStock = pack.reduce((memo: number, packitem: VariantPackItem) => {
    const locationStock = Object.values(packitem.variant?.stockLocations ?? {}).reduce(
      (sum, loc) => sum + (loc?.stock ?? 0),
      0,
    );
    // If outofstock flag is explicitly true, treat as 0 stock
    if (packitem.variant?.outofstock?.content?.value === true) return memo;
    // If stock locations exist, use their sum; otherwise assume in stock
    return memo + (Object.keys(packitem.variant?.stockLocations ?? {}).length > 0 ? locationStock : 1);
  }, 0);

  return (
    <div className="buy-section flex gap-1 mt-4">
      <div className="product__qty__wrapper">
        <div
          className="product__qty__less flex flex-col justify-center"
          onClick={deincrementValue}
        >
          <span>-</span>
        </div>
        <input
          type="number"
          min={1}
          value={inputValue}
          onChange={(e) => {
            setInputValue(Number(e.target.value));
          }}
          inputMode="numeric"
          className="product__qty [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          ref={qty}
        />
        <div
          className="product__qty__more flex flex-col justify-center"
          onClick={incrementValue}
        >
          <span className="h-min">+</span>
        </div>
      </div>
      <button
        data-testid="add-to-cart-button"
        className="button button--large w-full relative overflow-hidden"
        onClick={handleClick}
        disabled={defaultStock < 1}
      >
        {/* Default label */}
        <span
          className={`transition-all duration-200 flex items-center justify-center w-full ${
            showTada || showOhNo ? "scale-0 opacity-0" : "scale-100 opacity-100"
          }`}
        >
          {_t(label)}
        </span>

        {/* Success: checkmark */}
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
            showTada ? "scale-100 opacity-100" : "scale-0 opacity-0"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>

        {/* Error: X */}
        <span
          aria-hidden="true"
          className={`absolute inset-0 flex items-center justify-center transition-all duration-200 ${
            showOhNo ? "scale-100 opacity-100" : "scale-0 opacity-0"
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
      </button>
    </div>
  );
};
